/* eslint-disable complexity */
/* eslint-disable react/jsx-no-literals */
import React, { useState } from 'react';
import Typography from 'carbon-react/lib/components/typography';
import Icon from 'carbon-react/lib/components/icon';
import { defineMessages, useIntl } from 'react-intl';

import styles from './insight-accordion.scss';

interface InsightAccordionProps {
  variant: 'success' | 'warning' | 'error';
  category?: string;
  scoreDelta?: number;
  children: React.ReactNode;
}

const statusMessages = defineMessages({
  healthy: { id: 'aiInsights.healthy', defaultMessage: 'Healthy' },
  toWatch: { id: 'aiInsights.toWatch', defaultMessage: 'To watch' },
  atRisk: { id: 'aiInsights.atRisk', defaultMessage: 'At risk' }
});

const STATUS_PILL: Record<
  'success' | 'warning' | 'error',
  {
    tagClass: string;
    message: typeof statusMessages[keyof typeof statusMessages];
    color: string;
    scoreDeltaClass: string;
  }
> = {
  success: {
    tagClass: styles.tagHealthy,
    message: statusMessages.healthy,
    color: '#00811F',
    scoreDeltaClass: styles.scoreDeltaSuccess
  },
  warning: {
    tagClass: styles.tagToWatch,
    message: statusMessages.toWatch,
    color: '#D64309',
    scoreDeltaClass: styles.scoreDeltaWarning
  },
  error: {
    tagClass: styles.tagAtRisk,
    message: statusMessages.atRisk,
    color: '#DB004E',
    scoreDeltaClass: styles.scoreDeltaError
  }
};

export const InsightAccordion = ({ variant, category, scoreDelta, children }: InsightAccordionProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const intl = useIntl();

  const statusIcon = variant === 'success' ? 'tick_circle' : variant === 'error' ? 'error' : 'warning';
  const pill = STATUS_PILL[variant];

  return (
    <div className={styles.insightAccordion}>
      <div className={styles.accordionHeader}>
        <div className={styles.headerLeft}>
          <span className={styles.statusIcon}>
            <Icon type={statusIcon} color={pill.color} />
          </span>
          {category && (
            <Typography fontSize="21px" fontWeight="500" lineHeight="1.25em" mb={0} className={styles.categoryLabel}>
              {category}
            </Typography>
          )}
          {scoreDelta !== undefined && (
            <Typography fontSize="21px" fontWeight="500" lineHeight="1.25em" mb={0} className={pill.scoreDeltaClass}>
              {scoreDelta >= 0 ? `+${scoreDelta}` : scoreDelta}
            </Typography>
          )}
        </div>

        <div className={styles.headerRight}>
          <span className={`${styles.statusTag} ${pill.tagClass}`}>{intl.formatMessage(pill.message)}</span>
          <button
            type="button"
            className={styles.toggleButton}
            onClick={() => setIsExpanded(prev => !prev)}
            aria-expanded={isExpanded}
          >
            <span className={styles.toggleIcon}>
              <Icon type={isExpanded ? 'hide' : 'view'} />
            </span>
            <span className={styles.toggleLabel}>
              {isExpanded
                ? intl.formatMessage({ id: 'aiInsights.hideDetails', defaultMessage: 'Hide details' })
                : intl.formatMessage({ id: 'common.viewDetails', defaultMessage: 'View Details' })}
            </span>
          </button>
        </div>
      </div>

      {isExpanded && (
        <>
          <hr className={styles.contentDivider} />
          <div className={styles.accordionContent}>{children}</div>
        </>
      )}
    </div>
  );
};
